<template>
  <div class="flex flex-col h-full">
    <div
      class="flex justify-between items-center border-b border-gray-200 py-5 px-16"
    >
      <p class="font-bold text-lg">
        <icon name="users" class="mr-2" />{{
          $t('settings.manageMembers.title')
        }}
        ({{ firstTotalCount }})
      </p>
    </div>
    <div class="py-8 px-16">
      <div class="w-full bg-white border shadow-sm rounded-lg mb-32 p-8">
        <div class="flex items-center justify-between mb-10">
          <div class="w-[40%] relative">
            <input
              type="text"
              :placeholder="$t('settings.manageMembers.searchPlaceholder')"
              class="w-full h-10 outline-none text-sm rounded-lg border border-gray-200 py-6 pl-10 pr-10"
              v-model="userSearch"
              maxlength="50"
            />
            <icon
              name="magnifying-glass"
              class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 fill-gray-500"
            />
          </div>
          <div class="flex items-center gap-4">
            <button
              @click="exportMembers()"
              class="flex items-center gap-2 h-9 text-sm border border-gray-200 shadow-sm rounded-lg pl-3 pr-4 cursor-pointer"
            >
              <icon name="download" class="w-5 h-5 fill-gray-600" />
              <p class="text-sm">{{ $t('settings.manageMembers.export') }}</p>
            </button>
            <button
              @click="showUserInviteModal = true"
              class="flex items-center gap-2 bg-secondary h-9 text-sm text-white shadow-sm rounded-lg pl-3 pr-4 cursor-pointer"
            >
              <icon name="user-add" class="w-5 h-5 fill-white" />
              <p class="text-sm">
                {{ $t('settings.manageMembers.inviteUser') }}
              </p>
            </button>
          </div>
        </div>
        <div class="flex flex-col border border-gray-200 rounded-lg">
          <div class="flex items-center border-b border-gray-200 py-2">
            <div class="w-[30%] p-3">
              <p class="font-medium pl-3">
                {{ $t('settings.manageMembers.pseudo') }}
              </p>
            </div>
            <div class="w-[10%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageMembers.points') }}
              </p>
            </div>
            <div class="w-[10%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageMembers.badges') }}
              </p>
            </div>
            <div class="w-[20%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageMembers.registration') }}
              </p>
            </div>
            <div class="w-[15%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageMembers.status') }}
              </p>
            </div>
            <div class="w-[15%] p-3">
              <p class="font-medium">
                {{ $t('settings.manageMembers.actions') }}
              </p>
            </div>
          </div>
          <div
            v-for="(member, index) in members"
            :key="member.did"
            class="flex items-center border-b border-gray-200 py-2"
            :class="{
              'border-b-0': index == members.length - 1,
            }"
          >
            <div class="w-[30%] flex items-center gap-2 p-3">
              <img :src="member.imageBase64" class="w-10 h-10 rounded-full" />
              <p class="pl-2 break-all">{{ member.pseudo }}</p>
            </div>
            <div class="w-[10%] p-3">
              <p class="">{{ member.points }}</p>
            </div>
            <div class="w-[10%] p-3">
              <p class="pl-3">{{ member.badgeCount }}</p>
            </div>
            <div class="w-[20%] p-3">
              <p class="">
                {{
                  new Date(member.created).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                }}
              </p>
            </div>
            <div class="w-[15%] p-3">
              <p class="pl-2">
                <icon name="check" class="w-7 h-7 fill-blue-400" />
              </p>
            </div>
            <div class="w-[15%] flex items-center gap-4 p-3">
              <div
                class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer"
              >
                <icon name="chat-dots" class="w-5 h-5 fill-gray-500" />
              </div>
              <div
                class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer"
              >
                <icon name="paper-plane" class="w-5 h-5 fill-gray-500" />
              </div>
            </div>
          </div>
          <div v-if="members.length == 0" class="flex items-center p-4">
            <p class="text-gray-500 text-sm">
              {{ $t('settings.manageMembers.noMembers') }}
            </p>
          </div>
        </div>
        <div class="flex items-center justify-between mt-5">
          <p class="text-sm text-gray-500">
            <span class="font-bold text-blue-500">{{ currentPage }}</span>
            {{ $t('settings.manageMembers.of') }} {{ totalPages }}
            {{ $t('settings.manageMembers.pages') }}
          </p>
          <div class="flex items-center gap-2">
            <div class="flex items-center gap-2 mr-2">
              <p class="text-sm text-gray-500">
                {{ $t('settings.manageMembers.pageOn') }}
              </p>
              <select
                v-model="currentPage"
                class="w-14 h-9 px-2 text-sm leading-none border border-gray-200 rounded-lg cursor-pointer text-gray-500"
                @change="getCommunityMembers()"
              >
                <option v-for="page in totalPages" :key="page" :value="page">
                  {{ page }}
                </option>
              </select>
            </div>
            <div
              class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer"
              @click="changePage(false)"
            >
              <icon name="chevron-left" class="w-5 h-5 fill-gray-500" />
            </div>
            <div
              class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg cursor-pointer"
              @click="changePage(true)"
            >
              <icon
                name="chevron-left"
                class="w-5 h-5 fill-gray-500 rotate-180"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showUserInviteModal">
    <div class="fixed top-0 left-0 w-full h-full z-10">
      <div class="absolute w-full h-full bg-black bg-opacity-20"></div>
    </div>
    <BasicSmModal @close="showUserInviteModal = false" absoluteClose>
      <p class="font-bold mb-7">{{ $t('settings.manageMembers.invite') }}</p>
      <div class="flex items-center gap-2">
        <input
          type="text"
          :placeholder="$t('settings.manageMembers.emailPlaceholder')"
          class="w-[75%] h-8 text-sm outline-none rounded-lg border border-gray-200"
          v-model="userEmailsToInvite"
          maxlength="50"
        />
        <button
          @click="inviteUsers()"
          class="w-[25%] h-8 bg-secondary text-sm text-white shadow-sm font-medium rounded-lg px-3 cursor-pointer"
          :class="{
            'opacity-50 cursor-not-allowed pointer-events-none':
              userEmailsToInvite.length === 0,
          }"
        >
          {{ $t('settings.manageMembers.invite') }}
        </button>
      </div>
    </BasicSmModal>
  </div>
</template>

<script>
import { useStore } from 'vuex';
import { computed, ref, inject, watch } from 'vue';
import BasicSmModal from '../../components/BasicSmModal.vue';
import { useI18n } from 'vue-i18n';

export default {
  setup() {
    const store = useStore();
    const utils = inject('utils');
    const api = inject('api');
    const { t } = useI18n();
    const isWeb2Connexion = computed(() => store.getters.isWeb2Connexion);
    const membershipTemplateList = computed(
      () => store.getters.getMembershipTemplateList
    );

    const currentPage = ref(1);
    const pageSize = ref(5);
    const totalPages = ref(0);
    const totalCount = ref(0);
    const userSearch = ref('');
    const firstTotalCount = ref(null);
    const showUserInviteModal = ref(false);
    const userEmailsToInvite = ref('');
    const waitingSignature = ref(null);

    const members = ref([]);

    watch(userSearch, () => {
      getCommunityMembers();
    });

    async function getCommunityMembers() {
      api
        .getCommunityMembers(
          currentPage.value,
          pageSize.value,
          userSearch.value
        )
        .then((res) => {
          members.value = res.data.members;
          totalPages.value = res.data.pagination.totalPages;
          totalCount.value = res.data.pagination.totalCount;
          if (firstTotalCount.value == null) {
            firstTotalCount.value = totalCount.value;
          }
        });
    }
    getCommunityMembers();

    async function changePage(direction) {
      if (direction && currentPage.value < totalPages.value) {
        currentPage.value++;
        getCommunityMembers();
      } else if (!direction && currentPage.value > 1) {
        currentPage.value--;
        getCommunityMembers();
      }
    }

    async function exportMembers() {
      api.getAllCommunityMembers().then((res) => {
        // trigger CSV download with res.data.members
        const csvContent = ['Pseudo,Points,Badges,Registration,Did']
          .concat(
            res.data.members.map(
              (member) =>
                `${member.pseudo},${member.points},${member.badgeCount},${member.created},${member.did}`
            )
          )
          .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'members.csv';
        a.click();
        URL.revokeObjectURL(url);
      });
    }

    async function inviteUsers() {
      if (window._paq)
        window._paq.push([
          'trackEvent',
          `Template Membership`,
          'Send multiple custom links',
          'Confirm',
        ]);

      // filter emails
      let validEmails = [];
      const emailRegex = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
      validEmails = userEmailsToInvite.value
        .split(',')
        .filter((email) => emailRegex.test(email));

      if (validEmails.length == 0) {
        utils.showToastMessageError(t('settings.manageMembers.noValidEmails'));
        return;
      }

      showUserInviteModal.value = false;
      userEmailsToInvite.value = '';

      if (!isWeb2Connexion.value) {
        waitingSignature.value = 'signDelegation';
      } else {
        waitingSignature.value = 'basicLoader';
      }

      let endDate =
        new Date(new Date().getTime() + 30 * 24 * 60 * 60 * 1000)
          .toISOString()
          .slice(0, 19) + 'Z';

      api
        .createSession({
          templateId: membershipTemplateList.value[0].id,
          didLimit: 1,
          sessionNumber: validEmails.length,
          emails: validEmails,
          endDate,
        })
        .then(() => {
          if (window._paq)
            window._paq.push([
              'trackEvent',
              `Template Membership`,
              'Send multiple custom links',
              'Success',
            ]);
          waitingSignature.value = null;
        })
        .catch((err) => {
          if (err.response?.data?.code == 2000) {
            utils.showToastMessageError(
              t('settings.manageMembers.tooManyEmails')
            );
          } else {
            utils.showToastMessageError(t('other.serverError'));
          }
          waitingSignature.value = null;
        });
    }

    return {
      members,
      currentPage,
      pageSize,
      totalPages,
      totalCount,
      firstTotalCount,
      changePage,
      getCommunityMembers,
      userSearch,
      showUserInviteModal,
      userEmailsToInvite,
      inviteUsers,
      membershipTemplateList,
      exportMembers,
    };
  },
  components: {
    BasicSmModal,
  },
};
</script>
